import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, Cell, XAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { BarChart3 } from "lucide-react";
import api from "@/lib/api";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Last 12 mo", hint: "Last 365 session days" },
  { key: "ytd", label: "YTD", hint: "Since Jan 1" },
];

const fmtPnl = (v) => {
  if (!v) return '$0.00';
  const sign = v > 0 ? '+' : '-';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pnlCls = (v) => v > 0 ? 'ops-long' : v < 0 ? 'ops-short' : 'text-[hsl(var(--ops-text-muted))]';
const barFill = (v) => v > 0 ? 'hsl(var(--ops-long))' : v < 0 ? 'hsl(var(--ops-short))' : 'hsl(var(--ops-text-muted))';
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const DayTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[hsl(var(--ops-surface-2))] border border-[hsl(var(--ops-border))] rounded-lg px-3 py-2 text-xs">
      <div className="text-[hsl(var(--ops-text-muted))] mb-1">{fmtDay(d.date)}</div>
      <div className={`font-mono-num font-semibold ${pnlCls(d.pnl)}`}>{fmtPnl(d.pnl)}</div>
      <div className="font-mono-num text-[hsl(var(--ops-text-muted))]">{d.trades} trade{d.trades === 1 ? '' : 's'}</div>
    </div>
  );
};

// Realized P&L from closed trades (GET /api/ops/pnl-summary), one account type only — never SIM + LIVE mixed.
// Days follow the CME futures session (6 PM–5 PM ET), dated by the day the session ends, like NinjaTrader.
export default function PnlSummary({ autoRefresh = true, accountType = "SIM", title = "P&L · Paper (Sim101)" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/ops/pnl-summary", { params: { account_type: accountType } });
        setData(d);
      } catch (e) { /* silent */ }
    };
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [autoRefresh, accountType]);

  if (!data) return null;
  const empty = !data.closed_trades;

  return (
    <Card data-testid="ops-pnl-summary" className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
      <div className="flex items-center justify-between mb-2 max-sm:flex-wrap max-sm:gap-x-2 max-sm:gap-y-0.5">
        <span className="text-sm font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2">
          <BarChart3 className="size-4 text-[hsl(var(--ops-brand))]" /> {title}
        </span>
        <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">Realized · closed trades</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {PERIODS.map(({ key, label, hint }, i) => {
          const p = data.periods?.[key] || {};
          return (
            <div
              key={key}
              data-testid={`ops-pnl-${key}`}
              title={hint}
              className={`rounded-md bg-[hsl(var(--ops-surface-2))] px-2.5 py-2 min-w-0 ${i === PERIODS.length - 1 ? 'col-span-2 sm:col-span-1' : ''}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))]">{label}</div>
              <div className={`text-base font-semibold font-mono-num truncate ${pnlCls(p.pnl)}`}>{fmtPnl(p.pnl)}</div>
              <div className="text-[11px] font-mono-num text-[hsl(var(--ops-text-muted))]">
                {p.trades || 0} trade{p.trades === 1 ? '' : 's'}{p.win_rate != null && ` · ${p.win_rate}% win`}
              </div>
            </div>
          );
        })}
      </div>

      {empty ? (
        <div className="mt-3 h-16 flex items-center justify-center text-xs text-[hsl(var(--ops-text-muted))]">No closed trades yet</div>
      ) : (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-1">Daily · last 30 days (sessions)</div>
          <div className="h-[90px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" tickFormatter={fmtDay} interval="preserveStartEnd" minTickGap={24} tick={{ fill: 'hsl(var(--ops-text-muted))', fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<DayTooltip />} cursor={{ fill: 'hsl(var(--ops-surface-2))' }} />
                <ReferenceLine y={0} stroke="hsl(var(--ops-border))" />
                <Bar dataKey="pnl" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                  {data.daily.map((d) => <Cell key={d.date} fill={barFill(d.pnl)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-2 text-[10px] text-[hsl(var(--ops-text-muted))]">Days follow the futures session (6 PM–5 PM ET)</div>
    </Card>
  );
}
